import Immutable from 'immutable'
import color from 'color'
import postcss from 'postcss'
import postcssContrastColorFunction from '@csstools/postcss-contrast-color-function'
import valueParser from 'postcss-value-parser'

// Note: Keep in sync with fusion-backend-ts/src/utils/themes
export const themeGroupDelimiter = ':'

/**
 * Gets the path to a stylesheet for given theme via `themeName`.
 * Defaults to the main stylesheet path when `fileNameSuffix` is not given.
 * Example file name suffix: "-ep6" for the theme's checkout and customer area stylesheet.
 *
 * Note: Keep in sync with fusion-backend-ts/src/utils/themes
 *
 * @param themeName - The theme name (e.g., "epages.dimension:bodycare")
 * @param hashedThemeAssets - Map of hashed asset names from webpack stats
 * @param fileNameSuffix - Optional suffix for the filename (e.g., "-ep6")
 */
export function getThemeStylesheetPath(
  themeName: string,
  hashedThemeAssets: ImmutableMap,
  fileNameSuffix = '',
): string {
  // File name convention:
  // If it's a standalone theme, default file name to "style".
  // If it's a theme inside a theme group directory, look for a file with its scoped name.
  const [directoryName, fileName = 'style'] = themeName.split(themeGroupDelimiter)

  const stylesheetKey = `${directoryName}/${fileName}${fileNameSuffix}`
  const hashedFileName = hashedThemeAssets.getIn(['stylesheets', stylesheetKey])

  return `/themes/${directoryName}/stylesheets/${hashedFileName}`
}

/**
 * Gets the preview image path for given theme.
 *
 * @param themeName Name of the theme to get the preview image for.
 * @param hashedThemeAssets The theme's hashed theme assets from store state.
 */
export function getThemePreviewImagePath(themeName: string, hashedThemeAssets: ImmutableMap): string {
  // File name convention:
  // If it's a standalone theme, default file name to "preview".
  // If it's a theme inside a theme group directory, look for a file with its scoped name.
  const [directoryName, fileName = 'preview'] = themeName.split(themeGroupDelimiter)
  const hashedFileName = hashedThemeAssets.getIn([`${directoryName}/img`, `${fileName}.jpg`])

  return `/themes/${directoryName}/img/${hashedFileName}`
}

/**
 * Mapping of old header type names with their name.
 *
 * This is needed until the old header type names are no longer in use
 * (i.e. not in the database of any platform).
 */
const replacementHeaderTypes: Record<string, string> = {
  neutral: 'threeRows',
  vision: 'oneRowLogoLeft',
  twoRowsNestedMenu: 'twoRowsLogoLeft',
  uptown: 'twoRowsLogoLeft',
  editorial: 'twoRowsLogoLeft',
  essence: 'twoRowsLogoLeft',
  structure: 'twoRowsLogoLeft',
  limelight: 'twoRowsLogoLeft',
}

/**
 * Mapping of new color keys to old color keys by theme.
 *
 * This is needed until the old color keys are no longer in use
 * (i.e. not in the database of any platform).
 *
 * When the schema uses new keys but old shops have old keys in the database,
 * this mapping allows us to look up the old values.
 * Structure: { [themeName]: { [newKey]: oldKey } }
 */
const oldColorKeysByTheme: Record<string, Record<string, string>> = {
  // NOTE: Needs to be updated correspondingly by designers.
  'epages.limelight:current': {
    color1: 'primaryColor',
    color2: 'headerBackground',
    color3: 'footerColor',
  },
  // Add mappings for other themes as needed
}

/**
 * Mapping of new font keys to old color keys by theme.
 *
 * This is needed until the old font keys are no longer in use
 * (i.e. not in the database of any platform).
 *
 * When the schema uses new keys but old shops have old keys in the database,
 * this mapping allows us to look up the old values.
 * Structure: { [themeName]: { [newKey]: oldKey } }
 */
const oldFontKeysByTheme: Record<string, Record<string, string>> = {
  // NOTE: Needs to be updated correspondingly by designers.
  // Could be changed to a simpler check when it's the same for all themes.
  'epages.limelight:current': {
    headline1: 'headingFont',
  },
  // Add mappings for other themes as needed
}

/**
 * Gets the normalized header type name for use as class name, etc.
 *
 * This is needed until the old header type names are no longer in use
 * (i.e. not in the database of any platform).
 */
export function getNormalizedHeaderType(headerType: string): string {
  return replacementHeaderTypes[headerType] || headerType
}

/**
 * Returns a PostCSS processor that resolves all `var(--X)` references and
 * `contrast-color()` functions against the given computed theme settings.
 */
export function buildThemeCssProcessor(computedThemeSettings: Theme.Settings) {
  const flatVars = createFlatObjectWithPath(Immutable.fromJS(computedThemeSettings))
  const varResolverPlugin: postcss.Plugin = {
    postcssPlugin: 'resolve-theme-vars',
    Declaration(decl) {
      decl.value = resolveVars(decl.value, flatVars)
    },
  }

  return postcss([varResolverPlugin, postcssContrastColorFunction({ preserve: false })])
}

type ControlPoint = {
  variant: number
  lightness: number
}

// Define color variant control points (variant: lightness adjustment)
// Lighter, Light, Base, Dark, Darker
const controlPoints: ControlPoint[] = [
  { variant: 100, lightness: +35 },
  { variant: 300, lightness: +15 },
  { variant: 500, lightness: 0 },
  { variant: 700, lightness: -15 },
  { variant: 900, lightness: -35 },
]
const minControlPoint = controlPoints[0]
const maxControlPoint = controlPoints.at(-1) as ControlPoint

export function resolveColorVariant(baseColorValue: string, variant: number): string {
  const baseColor = color(baseColorValue)

  // Clamp variant to valid range
  const clampedVariant = Math.max(minControlPoint.variant, Math.min(maxControlPoint.variant, variant))

  // Find the two control points to interpolate between
  let lower = minControlPoint
  let upper = maxControlPoint

  for (let i = 0; i < controlPoints.length - 1; i++) {
    if (clampedVariant >= controlPoints[i].variant && clampedVariant <= controlPoints[i + 1].variant) {
      lower = controlPoints[i]
      upper = controlPoints[i + 1]
      break
    }
  }

  // Linear interpolation between the two control points
  const range = upper.variant - lower.variant
  const position = range === 0 ? 0 : (clampedVariant - lower.variant) / range
  const lightnessAdjustment = lower.lightness + (upper.lightness - lower.lightness) * position

  // Get current lightness and apply adjustment
  const hsl = baseColor.hsl()
  const currentLightness = hsl.lightness()
  const newLightness = Math.max(0, Math.min(100, currentLightness + lightnessAdjustment))

  // Return adjusted color
  return hsl.lightness(newLightness).hex()
}

const SYSTEM_COLORS = {
  _dark: '#404040',
  _light: '#d0d0d0',
}
const systemColorNames = Object.keys(SYSTEM_COLORS)

/**
 * Resolves theme settings by merging current settings with defaults based on a schema.
 *
 * @param schema - Array of theme setting schema items defining the structure and types of settings
 * @param currentSettings - The current theme settings to resolve
 * @param defaultSettings - Default theme settings to use as fallback values
 * @param themeName - Name of the theme being resolved
 * @param options - Optional configuration object
 * @param options.computeValues - Whether to compute color variant values instead of keeping variant definitions
 * @returns Resolved theme settings with all values populated from current or default settings
 *
 * @remarks
 * Provides backward compatibility for old color and font key names based on theme-specific mappings.
 * For color groups, processes color definitions including their variants and system colors.
 */
export function resolveThemeSettings(
  schema: Theme.SettingsSchemaItem[],
  currentSettings: Theme.Settings,
  defaultSettings: Theme.Settings,
  themeName: string,
  options: { computeValues?: boolean } = {},
): Theme.Settings {
  const themeSettings: Theme.Settings = {}

  for (const schemaItem of schema) {
    const settingValue = currentSettings[schemaItem.name] ?? defaultSettings[schemaItem.name]

    if (schemaItem.type === 'group' || schemaItem.type === 'fontGroup') {
      themeSettings[schemaItem.name] = resolveThemeSettings(
        (schemaItem as Theme.GroupSettingsSchemaItem).settings,
        settingValue ?? {},
        defaultSettings[schemaItem.name] ?? {},
        themeName,
        options,
      )
    } else if (schemaItem.type === 'colorGroup') {
      const colorGroupSchemaItem = schemaItem as Theme.ColorGroupSettingsSchemaItem
      themeSettings[colorGroupSchemaItem.name] = {}

      const baseColorNames = [
        ...colorGroupSchemaItem.colors,
        ...colorGroupSchemaItem.additionalColors,
        ...systemColorNames,
      ]

      // Colors:
      const baseColors = {}
      for (const color of baseColorNames) {
        let baseColorValue: string | undefined = color in SYSTEM_COLORS ? SYSTEM_COLORS[color] : settingValue?.[color]

        // Compat: Support old names for color keys
        // E.g. Limelight theme color1 -> primaryColor
        // If it's a string, then we know it's an old key color value.
        // If it's an object, then the same key is also used as variant name (e.g. headerBackground in Limelight).
        const valueForOldKey: string | Theme.ColorGroupSettingsColorVariant =
          settingValue?.[oldColorKeysByTheme[themeName]?.[color]]
        if (typeof valueForOldKey === 'string') {
          baseColorValue = valueForOldKey
        }

        baseColors[color] =
          baseColorValue ||
          defaultSettings[colorGroupSchemaItem.name]?.[color] ||
          getThemeSettingDefaultValueFallback('color')
        themeSettings[colorGroupSchemaItem.name][color] = baseColors[color]
      }

      // Color variants:
      for (const variantDefinition of colorGroupSchemaItem.variants || []) {
        let variant: Theme.ColorGroupSettingsColorVariant | undefined =
          currentSettings[colorGroupSchemaItem.name]?.[variantDefinition.name] ||
          defaultSettings[colorGroupSchemaItem.name]?.[variantDefinition.name]

        // Compat
        // E.g. headerBackground in Limelight theme was previously a color key
        // fallback for compat: headerBackground case where variant name headerBackground
        // was a previous color key. In this case, `variant` is a string.
        if (typeof variant === 'string') {
          variant = defaultSettings[colorGroupSchemaItem.name]?.[variantDefinition.name]
        }

        if (variant) {
          if (options.computeValues) {
            themeSettings[colorGroupSchemaItem.name][variantDefinition.name] = resolveColorVariant(
              baseColors[variant.base],
              variant.variant,
            )
          } else {
            themeSettings[colorGroupSchemaItem.name][variantDefinition.name] = variant
          }
        }
      }
    } else {
      const compatValue: string | undefined =
        schemaItem.type === 'font' ? currentSettings[oldFontKeysByTheme[themeName]?.[schemaItem.name]] : undefined

      themeSettings[schemaItem.name] =
        compatValue ?? settingValue ?? getThemeSettingDefaultValueFallback(schemaItem.type)
    }
  }

  return themeSettings
}

// For the currently unused "selection" type, no dedicated default value is defined.
// It could be added here later if needed.
function getThemeSettingDefaultValueFallback(settingType: string) {
  switch (settingType) {
    case 'input':
    case 'customCss':
    case 'radioButtons':
      return ''
    case 'rangeWithInput':
      return 50
    case 'color':
      return 'transparent'
    case 'font':
      return 'sans-serif'
    case 'checkbox':
      return false
    default:
      return null
  }
}

/**
 * Given an Immutable.Map, this function creates a flat object from it,
 * preserving the nesting structure of the individual keys as the key of the
 * resulting flat object.
 */
export function createFlatObjectWithPath(map: ImmutableMap, path = '', delimiter = '-'): Record<string, unknown> {
  return map.reduce((acc: Record<string, unknown>, val, key) => {
    const computedPath = (path ? `${path}${delimiter}` : '') + key

    return typeof val !== 'object' || val === null
      ? { ...acc, [computedPath]: val }
      : { ...acc, ...createFlatObjectWithPath(val, computedPath, delimiter) }
  }, {})
}

/**
 * Replaces `var(--X)` and `var(--X, fallback)` calls in a CSS value expression
 * with their resolved values from `flatVars`. Handles arbitrary nesting, e.g.
 * `var(--X, contrast-color(var(--Y)))`.
 */
function resolveVars(expr: string, flatVars: Record<string, unknown>): string {
  const parsed = valueParser(expr)
  const replacements: Array<{ start: number; end: number; value: string }> = []

  parsed.walk((node) => {
    if (node.type !== 'function' || node.value !== 'var') return

    // Strip leading --
    const varName = node.nodes[0].value.slice(2)
    const resolvedValue = flatVars[varName]

    if (resolvedValue === undefined || typeof resolvedValue === 'object') return

    replacements.push({ start: node.sourceIndex, end: node.sourceEndIndex, value: String(resolvedValue) })

    // var() is fully replaced, skip descendants
    return false
  })

  // Process right-to-left to avoid invalidating source positions of later matches.
  // Example: for [{ start: 7, end: 28, value: '#fff' }, { start: 32, end: 51, value: '#000' }],
  // replacing at 7 first shifts position 32, so the second match lands in the wrong range.
  let result = expr
  for (const { start, end, value } of replacements.toReversed()) {
    result = result.slice(0, start) + value + result.slice(end)
  }

  return result
}

const contrastColorProcessor = postcss([postcssContrastColorFunction({ preserve: false })])

/**
 * Builds a CSS `:root { }` block declaring all resolved theme custom properties
 * and pre-computed contrast color values, for injection into SSR HTML and
 * client-side re-renders via ThemeStyleVariables.
 */
export function buildThemeVarsCss(
  themeSettingsSchema: Theme.SettingsSchema,
  defaultThemeSettings: Theme.Settings,
  themeName: string,
  themeSettings: Theme.Settings,
  contrastColorExpressionMap: Record<string, string>,
): string {
  const computed = resolveThemeSettings(
    filterOutCustomCss(themeSettingsSchema),
    themeSettings,
    defaultThemeSettings,
    themeName,
    {
      computeValues: true,
    },
  )
  const flatVars = createFlatObjectWithPath(Immutable.fromJS(computed))

  const varDecls = Object.entries(flatVars)
    .map(([varName, value]) => `--${varName}:${value}`)
    .join(';')

  const contrastDecls = Object.entries(contrastColorExpressionMap)
    .map(([key, expr]) => `${key}:${resolveVars(expr, flatVars)}`)
    .join(';')

  const themeVarsCss = `:root{${[varDecls, contrastDecls].filter(Boolean).join(';')}}`
  return contrastColorProcessor.process(themeVarsCss, { from: undefined }).css
}

const SOURCE_MAPPING_URL_RE = /\/\*# sourceMappingURL=(?:.*?)\*\//

/**
 * Returns processed CSS with the `sourceMappingURL` pragma that PostCSS strips re-appended.
 */
export function withSourceMapURL(rawCss: string, processedCss: string): string {
  const sourceMapLink = SOURCE_MAPPING_URL_RE.exec(rawCss)?.[0]
  return sourceMapLink ? `${processedCss}\n${sourceMapLink}` : processedCss
}

function filterOutCustomCss(schema: Theme.SettingsSchemaItem[]): Theme.SettingsSchemaItem[] {
  return schema
    .filter((item) => item.type !== 'customCss')
    .map((item) =>
      item.type === 'group'
        ? { ...item, settings: filterOutCustomCss((item as Theme.GroupSettingsSchemaItem).settings) }
        : item,
    )
}
